import { useEffect, useState } from "react";
import { useParams, useNavigate, Link } from "react-router-dom";
import axios from "axios";
import { CheckCircle2, XCircle, Loader2, Mail } from "lucide-react";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

function MagicLinkPage({ onLogin }) {
  const { token } = useParams();
  const navigate = useNavigate();
  const [status, setStatus] = useState("verifying"); // verifying, success, error
  const [error, setError] = useState("");

  useEffect(() => {
    if (token) {
      verifyToken();
    }
  }, [token]);

  const verifyToken = async () => {
    try {
      const response = await axios.get(`${API}/auth/magic-link/verify/${token}`);
      const { access_token, user } = response.data;
      
      setStatus("success");
      
      // Brief delay to show success message
      setTimeout(() => {
        onLogin(user, access_token);
        navigate("/dashboard");
      }, 1500);
      
    } catch (err) {
      setStatus("error");
      setError(err.response?.data?.detail || "Failed to verify login link. Please try again.");
    }
  };

  return (
    <div className="auth-container">
      <div className="container mx-auto px-4 py-16 flex items-center justify-center min-h-screen">
        <div className="auth-card w-full max-w-md p-8 text-center">
          {/* Logo */}
          <div className="mb-8">
            <img 
              src="/assets/lsbp-logo.png" 
              alt="The Lemonade Stand"
              className="mx-auto mb-4"
              style={{ maxWidth: "120px", height: "auto" }}
            />
          </div>

          {/* Verifying State */}
          {status === "verifying" && (
            <div data-testid="magic-link-verifying">
              <div className="flex justify-center mb-6">
                <div className="w-16 h-16 rounded-full bg-yellow-100 flex items-center justify-center">
                  <Loader2 className="w-8 h-8 text-yellow-500 animate-spin" />
                </div>
              </div>
              <h1 className="text-2xl font-bold text-gray-800 mb-2">
                Signing you in...
              </h1>
              <p className="text-gray-600">
                Please wait while we verify your login link.
              </p>
            </div>
          )}

          {/* Success State */}
          {status === "success" && (
            <div data-testid="magic-link-success">
              <div className="flex justify-center mb-6">
                <div className="w-16 h-16 rounded-full bg-green-100 flex items-center justify-center">
                  <CheckCircle2 className="w-8 h-8 text-green-500" />
                </div>
              </div>
              <h1 className="text-2xl font-bold text-gray-800 mb-2">
                Welcome back! 🍋
              </h1>
              <p className="text-gray-600">
                Redirecting you to your dashboard...
              </p>
            </div>
          )}

          {/* Error State */}
          {status === "error" && (
            <div data-testid="magic-link-error">
              <div className="flex justify-center mb-6">
                <div className="w-16 h-16 rounded-full bg-red-100 flex items-center justify-center">
                  <XCircle className="w-8 h-8 text-red-500" />
                </div>
              </div>
              <h1 className="text-2xl font-bold text-gray-800 mb-2">
                Link Invalid
              </h1>
              <p className="text-red-600 mb-6">
                {error}
              </p>
              <div className="space-y-3">
                <Link 
                  to="/login" 
                  className="lemon-button w-full py-3 rounded-lg text-white font-bold flex items-center justify-center gap-2"
                  data-testid="request-new-link-btn"
                >
                  <Mail className="w-5 h-5" />
                  Request New Login Link
                </Link>
                <Link 
                  to="/login"
                  className="block text-gray-600 hover:text-gray-800 text-sm"
                >
                  Or sign in another way
                </Link>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

export default MagicLinkPage;
